import { useState, useEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, Outlet, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "ui/Sidenav";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";

import { useMaterialUIController, setMiniSidenav } from "context";

import logo from "assets/images/identite/logo.png";
import routes from "router/routes";

import { LAST_PATH, USER_DATA_STORAGE } from "constant";
import SuspenceComponent from "components/SuspenceComponent";
import LayoutWrapper from "components/LayoutWrapper";

import {
  PATH_LOT,
  PATH_ANIMAUX,
  PATH_RACE,
  PATH_DETAIL_USER,
  PATH_DETAIL_ANIMAL,
  PATH_DETAIL_DEMANDE_IDENTIFICATION,
  PATH_NEW_DEMANDE_IDENTIFICATION,
  PATH_ENTITE_DETAIL,
} from "constant/moduleInfo";
import DetailUser from "layouts/gestionUsers/rubrique/DetailUser";

import DetailAnimal from "layouts/gestionAnimaux/DetailAnimal";
import DetailDemandeIdentification from "layouts/gestionAnimaux/DetailDemandeIdentification";

import NewDemandeIdentification from "layouts/gestionAnimaux/NewDemandeIdentification";

import DetailEntite from "layouts/donneeReference/rubrique/gestionEntite/DetailEntite";

const SignIn = lazy(() => import("layouts/authentication/sign-in"));

const Error404 = lazy(() => import("layouts/error404/Error404"));
const GestionLot = lazy(() => import("layouts/donneeReference/rubrique/gestionLot"));
const GestionRace = lazy(() => import("layouts/donneeReference/rubrique/gestionRace"));
const Logout = lazy(() => import("layouts/authentication/sing-out"));
const GestionAnimaux = lazy(() => import("layouts/gestionAnimaux"));

const routesData = routes;

const isSessionExpired = (userToken) => {
  const currentDate = new Date();
  if (userToken !== null && userToken.exp * 1000 < currentDate.getTime()) {
    return true;
  }
  return false;
};
const useAuth = () => {
  let userToken = null;
  if (localStorage.getItem(USER_DATA_STORAGE) !== null) {
    userToken = JSON.parse(localStorage.getItem(USER_DATA_STORAGE));
  }
  if (userToken !== null && userToken !== "" && userToken !== "{}" && userToken !== undefined) {
    const currentDate = new Date();
    if (userToken.exp * 1000 > currentDate.getTime()) {
      return true;
    }
    return false;
  }
  return false;
};
export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [defaultPath, setdefaultPath] = useState("/");
  const { pathname } = useLocation();
  const [isAuth, setIsAuth] = useState(false);
  const navigate = useNavigate();
  const setTabTitle = () => {
    let tabTitle = "BetaClic";
    if (localStorage.getItem(USER_DATA_STORAGE) !== null) {
      const userToken = JSON.parse(localStorage.getItem(USER_DATA_STORAGE));

      if (userToken !== null && userToken.entite !== null && userToken.entite !== undefined) {
        tabTitle = `BetaClic | ${userToken.entite}`;
      }
    }
    document.title = tabTitle;
  };
  useEffect(() => {
    const AuthBool = useAuth();
    setIsAuth(AuthBool);
    setTabTitle();
  });

  useEffect(() => {
    let userToken = null;
    if (localStorage.getItem(USER_DATA_STORAGE) !== null) {
      userToken = JSON.parse(localStorage.getItem(USER_DATA_STORAGE));
    }
    const AuthBool = useAuth();
    if (AuthBool) {
      let userRoles = [];
      if (userToken !== null) {
        userRoles = userToken.roles;
      }
      const dataRes = [];
      routesData.forEach((i, idx, arr) => {
        const isAvalaible = i.role
          .map((el1) => ({
            name: el1,
            match: userRoles.some((el2) => el2.roleName.toLowerCase() === el1.toLowerCase()),
          }))
          .some((x) => x.match === true);
        if (isAvalaible) {
          dataRes.push(i);
        }
        if (idx === arr.length - 1) {
          return dataRes;
        }
        return "success";
      });

      if (dataRes.length > 0 && dataRes[0].route) {
        setdefaultPath(dataRes[0].route);
        const lastPath = localStorage.getItem(LAST_PATH);
        if (lastPath === null) {
          if (Array.isArray(dataRes) && dataRes.length > 0) {
            navigate(dataRes[0].route);
          }
        }
      } else {
        alert(
          "Vous n'êtes pas authorisé à accéder à cette plateforme. Contactez votre administrateur"
        );
      }
    } else {
      console.log("Betaclic");
      if (userToken !== null && isSessionExpired(userToken)) {
        const tabTitle = "BetaClic";
        document.title = tabTitle;
        alert("Votre session a expirée");
      }
    }
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route?.collapse?.length > 0) {
        return getRoutes(route.collapse);
      }

      return (
        <Route
          path={route.route}
          element={<LayoutWrapper>{route.component}</LayoutWrapper>}
          key={route.key}
        />
      );
    });

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && isAuth && (
        <Sidenav
          color={sidenavColor}
          brand={logo}
          routes={routesData}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      <Routes>
        <Route exact path="/" element={<ProtectedRoutes />}>
          <Route path="/" element={<Navigate replace to={defaultPath} />} />
          {getRoutes(routesData)}
          <Route
            path="*"
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <Error404 />
              </Suspense>
            }
          />
          <Route
            path="/logout"
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <Logout />
              </Suspense>
            }
          />
          <Route
            path={PATH_LOT}
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <LayoutWrapper>
                  <GestionLot />
                </LayoutWrapper>
              </Suspense>
            }
          />
          <Route
            path={PATH_ANIMAUX}
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <LayoutWrapper>
                  <GestionAnimaux />
                </LayoutWrapper>
              </Suspense>
            }
          />
          <Route
            path={PATH_RACE}
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <LayoutWrapper>
                  <GestionRace />
                </LayoutWrapper>
              </Suspense>
            }
          />
          <Route
            path={PATH_DETAIL_USER}
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <LayoutWrapper>
                  <DetailUser />
                </LayoutWrapper>
              </Suspense>
            }
          />
          <Route
            path={PATH_DETAIL_ANIMAL}
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <LayoutWrapper>
                  <DetailAnimal />
                </LayoutWrapper>
              </Suspense>
            }
          />
          <Route
            path={PATH_DETAIL_DEMANDE_IDENTIFICATION}
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <LayoutWrapper>
                  <DetailDemandeIdentification />
                </LayoutWrapper>
              </Suspense>
            }
          />
          <Route
            path={PATH_NEW_DEMANDE_IDENTIFICATION}
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <LayoutWrapper>
                  <NewDemandeIdentification />
                </LayoutWrapper>
              </Suspense>
            }
          />
          <Route
            path={PATH_ENTITE_DETAIL}
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <LayoutWrapper>
                  <DetailEntite />
                </LayoutWrapper>
              </Suspense>
            }
          />
        </Route>

        <Route path="" element={<PublicRoutes />}>
          <Route
            path="/sign-in"
            element={
              <Suspense fallback={<SuspenceComponent />}>
                <SignIn />{" "}
              </Suspense>
            }
          />
        </Route>
      </Routes>
    </ThemeProvider>
  );
}
const ProtectedRoutes = () => {
  const auth = useAuth();

  return auth ? <Outlet /> : <Navigate to="/sign-in" />;
};
const PublicRoutes = () => {
  const dataRes = [];

  const auth = useAuth();

  if (auth) {
    const userToken = localStorage.getItem(USER_DATA_STORAGE);
    let userRoles = [];
    if (userToken !== null) {
      userRoles = JSON.parse(userToken).roles;
    }
    routesData.forEach((i, idx, arr) => {
      const isAvalaible = i.role
        .map((el1) => ({
          name: el1,
          match: userRoles.some((el2) => el2.roleName === el1),
        }))
        .some((x) => x.match === true);
      if (isAvalaible) {
        dataRes.push(i);
      }
      if (idx === arr.length - 1) {
        return dataRes;
      }
      return "success";
    });
  }
  return dataRes.length > 0 ? <Navigate to={dataRes[0].route} /> : <Outlet />;
};
